import { type ReactNode, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { trpc } from './utils/trpc';
import { supabase } from './utils/supabase';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VenueProvider } from './contexts/VenueContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FeedbackModal } from './components/FeedbackModal';

import Landing from './page-components/Landing';
import AccountOptions from './page-components/AccountOptions';
import SignIn from './page-components/SignIn';
import PasswordReset from './page-components/PasswordReset';
import { GroupMemberSignUp } from './page-components/GroupMemberSignUp';
import { SignupVenue } from './page-components/SignupVenue';

import MemberProfile from './page-components/MemberProfile';
import GroupMemberProfile from './page-components/GroupMemberProfile';
import { GroupMemberAccountEdit } from './page-components/GroupMemberAccountEdit';
import TagSelection from './page-components/TagSelection';
import MemberNotification from './page-components/MemberNotification';

import VenueProfile from './page-components/VenueProfile';
import VenueOwnerProfile from './page-components/VenueOwnerProfile';
import { VenueOwnerAccountEdit } from './page-components/VenueOwnerAccountEdit';
import CreateVenue from './page-components/CreateVenue';
import VenueEditor from './page-components/EditVenue';
import VenueNotification from './page-components/VenueNotification';

import GroupPage from './page-components/GroupPage';
import CreateGroup from './page-components/CreateGroup';
import EditGroup from './page-components/EditGroup';
import { GroupEvents } from './page-components/GroupEvents';
import GroupJoinRequests from './page-components/GroupJoinRequests';

import ManageVenues from './page-components/ManageVenues';

import CreateEvent from './page-components/CreateEvent';
import { NavColumn } from './components/NavColumn';
import { MapPage } from './page-components/MapPage';
import { Social } from './page-components/Social';
import { Settings } from './page-components/Settings';
import { NavBar } from './components/NavBar';

// ─── Layouts ──────────────────────────────────────────────────────────────────

function PublicLayout({ children }: { children: ReactNode; showBanner?: boolean }) {
  return <div id="public-pages">{children}</div>;
}

function SignedInLayout({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <div id="signed-in-pages">{children}</div>
    </ProtectedRoute>
  );
}

function GuestRoute({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const memberProfileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });
  const isMember = !!memberProfileQuery.data;
  const venueOwnerProfileQuery = trpc.venueOwner.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });
  const isVenue = !!venueOwnerProfileQuery.data;

  return user ? (
    isMember ? (
      <Navigate to="/explore" replace />
    ) : isVenue ? (
      <Navigate to="/manage-venues" replace />
    ) : (
      children
    )
  ) : (
    children
  );
}

function MemberRoute({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const memberProfileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  if (!session || memberProfileQuery.isLoading) return null;

  return (
    <div id="MemberLayout">
      {isMobile ? <NavBar /> : <NavColumn />}
      {memberProfileQuery.data ? children : <Navigate to="/manage-venues" replace />}
    </div>
  );
}

function VenueRoute({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const venueOwnerProfileQuery = trpc.venueOwner.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });
  const isVenue = !!venueOwnerProfileQuery.data;
  if (!session || venueOwnerProfileQuery.isLoading) return null;
  return isVenue ? children : <Navigate to="/explore" replace />;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: import.meta.env.VITE_API_URL!,
          async headers() {
            const { data } = await supabase.auth.getSession();
            const token = data.session?.access_token;

            return {
              Authorization: token ? `Bearer ${token}` : undefined,
            };
          },
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <VenueProvider>
            <BrowserRouter>
              <FeedbackModal></FeedbackModal>
              <Routes>
                {/* ── Public ── */}
                <Route path="/dashboard" element={<Navigate to="/explore" replace />} />
                <Route path="/" element={<Landing />} />
                <Route
                  path="/sign-in"
                  element={
                    <PublicLayout>
                      <GuestRoute>
                        <SignIn />
                      </GuestRoute>
                    </PublicLayout>
                  }
                />
                <Route
                  path="/sign-up"
                  element={
                    <PublicLayout>
                      <GuestRoute>
                        <AccountOptions />
                      </GuestRoute>
                    </PublicLayout>
                  }
                />
                <Route path="/sign-up/member" element={<GroupMemberSignUp />} />
                <Route
                  path="/sign-up/venue"
                  element={
                    <GuestRoute>
                      <SignupVenue />
                    </GuestRoute>
                  }
                />
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route
                  path="/members/:username"
                  element={
                    <PublicLayout>
                      <MemberProfile />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/venues/:venueName"
                  element={
                    <PublicLayout>
                      <VenueProfile />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/groups/:id"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupPage />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/tag-selection"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <TagSelection />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />

                {/* ── Discover ── */}
                <Route
                  path="/explore"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <MapPage />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />

                {/* ── Member ── */}
                <Route
                  path="/group-member/profile"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupMemberProfile />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/group-member/edit"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupMemberAccountEdit />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <Settings />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <MemberNotification />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />

                {/* ── Venue Owner ── */}
                <Route
                  path="/venue-owner/profile"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <VenueOwnerProfile />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/venue-owner/edit"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <VenueOwnerAccountEdit />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/venue-owner/notifications"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <VenueNotification />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/venues/create"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <CreateVenue />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/venues/:venueId/edit"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <VenueEditor />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/manage-venues"
                  element={
                    <SignedInLayout>
                      <VenueRoute>
                        <ManageVenues />
                      </VenueRoute>
                    </SignedInLayout>
                  }
                />

                {/* ── Groups ── */}
                <Route path="/groups/:id" element={<GroupPage />} />
                <Route
                  path="/create-group"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <CreateGroup />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/groups/:groupId/edit"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <EditGroup />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/groups/:groupId/events"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupEvents />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/groups/:id/requests"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupJoinRequests />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/social"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <Social />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/group/:id/:groupname"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupPage />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/group/:id"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <GroupPage />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
                <Route
                  path="/groups/:groupId/create-event"
                  element={
                    <SignedInLayout>
                      <MemberRoute>
                        <CreateEvent />
                      </MemberRoute>
                    </SignedInLayout>
                  }
                />
              </Routes>
            </BrowserRouter>
          </VenueProvider>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
